import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateContract } from "@/hooks/api/useContracts";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(v);
}

// The full itemised cost picture — legacy kept these as discrete lines so an
// invoice could reproduce the breakdown rather than a single summed total.
const FIELDS = [
  { key: "rental_price", label: "Base rent" },
  { key: "service_costs", label: "Service costs" },
  { key: "gwl_costs", label: "Gas / water / electricity" },
  { key: "depreciation_upholstery", label: "Upholstery depreciation" },
  { key: "tv_internet_fee", label: "TV / internet" },
  { key: "municipality_fee", label: "Municipality charges" },
  { key: "cleaning_fee", label: "Cleaning" },
  { key: "other_costs", label: "Other costs" },
  { key: "deposit_amount", label: "Deposit" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

// What each amount drives downstream — surfaced inline so the effect of a number isn't a
// mystery (the user asked for "what entered data impacts what").
const IMPACT: Record<FieldKey, string> = {
  rental_price: "Drives the monthly rent invoice and is the base for indexation.",
  service_costs: "An advance the tenant pays; reconciled in the annual afrekening.",
  gwl_costs: "Advance for gas/water/electricity — added to the monthly invoice, settled in the afrekening.",
  depreciation_upholstery:
    "Monthly charge for furnishing/stoffering — added to the monthly invoice.",
  tv_internet_fee: "Monthly TV/internet charge — added to the monthly invoice.",
  municipality_fee: "Monthly municipal levies passed on — added to the monthly invoice.",
  cleaning_fee: "Monthly cleaning charge — added to the monthly invoice.",
  other_costs: "Any remaining monthly charge — added to the monthly invoice.",
  deposit_amount: "The agreed waarborgsom — invoiced, held and settled on the Deposit tab.",
};

export function CostsCard({ contract }: { contract: Contract }) {
  const update = useUpdateContract(contract.id);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<FieldKey, string>>(() =>
    Object.fromEntries(
      FIELDS.map((f) => [f.key, contract[f.key] != null ? String(contract[f.key]) : ""]),
    ) as Record<FieldKey, string>,
  );

  const total =
    (contract.rental_price ?? 0) +
    (contract.service_costs ?? 0) +
    (contract.gwl_costs ?? 0) +
    (contract.depreciation_upholstery ?? 0) +
    (contract.tv_internet_fee ?? 0) +
    (contract.municipality_fee ?? 0) +
    (contract.cleaning_fee ?? 0) +
    (contract.other_costs ?? 0);

  // No cost is meaningfully negative; reject negatives / non-numbers client-side.
  const invalid = FIELDS.some((f) => {
    const raw = form[f.key].trim();
    if (raw === "") return false;
    const n = Number(raw);
    return !Number.isFinite(n) || n < 0;
  });

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid) return;
    const body: Record<string, number | null> = {};
    for (const f of FIELDS) {
      const raw = form[f.key].trim();
      body[f.key] = raw === "" ? null : Number(raw);
    }
    update.mutate(body, {
      onSuccess: () => {
        toast.success("Costs updated");
        setEditing(false);
      },
      onError: () => toast.error("Could not update costs"),
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Costs breakdown</CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label} (€)</Label>
                  <Input
                    id={f.key}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                  {IMPACT[f.key] && (
                    <p className="text-xs text-muted-foreground">{IMPACT[f.key]}</p>
                  )}
                </div>
              ))}
            </div>
            {invalid && (
              <p className="text-xs text-destructive">
                Amounts can't be negative.
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending || invalid}>
                {update.isPending ? "Saving…" : "Save costs"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <dl className="divide-y divide-border/60 text-sm">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between py-2">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="tabular-nums">{euro(contract[f.key])}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between py-2 font-medium">
              <dt>Monthly total (rent + costs)</dt>
              <dd className="tabular-nums">{euro(total)}</dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
